import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert, Button, Card, CardContent, IconButton, Stack, TextField, Tooltip, Typography
} from '@mui/material';
import {
  useLazyGetEnrolmentRequestQuery, useUpdateEnrolmentDetailsMutation, type InstallationStatus
} from '@store/api/installationEndpoints';
import { serverMessage } from '@store/error';
import { useEffect, useState, type FormEvent } from 'react';

interface EnrolmentDetailsCardProps {
  installation?: InstallationStatus;
}

/**
 * The two things only the customer knows, and the request built from them.
 *
 * **These are the customer's values and CDMVision signs them as they stand.** An operator checks
 * them against the order and either issues or asks for a new request; nobody at our end edits
 * them, so this screen is the only place they are decided. Saying that matters more than it looks:
 * an administrator who thinks a saved change takes effect immediately will move DNS to match, and
 * moving DNS before the new certificate is installed stops every device at once.
 *
 * The download sits beside Save because the request *is* these two values — there is nothing else
 * in it a person chose. It is disabled while the form has unsaved changes, so nobody edits a name
 * and then mails a request still carrying the old one.
 */
const EnrolmentDetailsCard = ({ installation }: EnrolmentDetailsCardProps) => {
  const [updateEnrolmentDetails, { isLoading }] = useUpdateEnrolmentDetailsMutation();
  const [fetchEnrolmentRequest] = useLazyGetEnrolmentRequestQuery();

  const [serverName, setServerName] = useState('');
  const [hosts, setHosts] = useState<string[]>(['']);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);

  // Seeded from the server rather than held locally, so the form shows what was actually stored
  // and not what somebody typed into a tab they left open.
  useEffect(() => {
    if (!installation) return;

    setServerName(installation.requestedName ?? '');
    setHosts(installation.requestedHosts.length > 0 ? installation.requestedHosts : ['']);
  }, [installation]);

  const certificate = installation?.certificate;
  const savedName = installation?.requestedName ?? '';
  const savedHosts = installation?.requestedHosts ?? [];

  const cleanedHosts = hosts.map(host => host.trim()).filter(Boolean);

  // Compared against what the server holds, not against a "touched" flag: typing a character and
  // deleting it again is not an unsaved change, and telling somebody it is teaches them to ignore
  // the warning.
  const isDirty = serverName.trim() !== savedName
    || cleanedHosts.length !== savedHosts.length
    || cleanedHosts.some((host, index) => host.toLowerCase() !== savedHosts[index]);

  const canDownload = !!savedName && savedHosts.length > 0 && !isDirty;

  // A certificate names the hosts devices actually check. If what is saved has drifted from it,
  // that difference is the whole story: a new enrolment is owed, and until it arrives the
  // certificate's hosts are still the live ones.
  const certificateHosts = certificate?.installed ? certificate.permittedHosts ?? [] : [];
  const differsFromCertificate = certificateHosts.length > 0
    && (savedHosts.length !== certificateHosts.length
      || savedHosts.some(host => !certificateHosts.includes(host)));

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    setSaved(false);

    try {
      await updateEnrolmentDetails({ serverName: serverName.trim(), hosts: cleanedHosts }).unwrap();
      setSaved(true);
    } catch (caught) {
      setError(serverMessage(caught) ?? 'The enrollment details could not be saved.');
    }
  };

  const onDownloadRequest = async () => {
    const content = await fetchEnrolmentRequest().unwrap();

    const url = URL.createObjectURL(new Blob([content], { type: 'application/json' }));
    const anchor = document.createElement('a');

    anchor.href = url;
    // A name, like the route it comes from and the one the server's own download carries, so it
    // keeps its spelling while the text around it says enrollment.
    anchor.download = 'enrolment-request.json';
    anchor.click();

    URL.revokeObjectURL(url);
  };

  const setHost = (index: number, value: string) =>
    setHosts(current => current.map((host, i) => i === index ? value : host));

  const removeHost = (index: number) =>
    setHosts(current => current.filter((_, i) => i !== index));

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant='h6' gutterBottom>Enrollment details</Typography>

        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          CDMVision signs these into your server certificate exactly as you set them here, so what
          this screen shows and what your certificate says are always the same. They are checked
          rather than corrected: if a name or a host is not acceptable, we will ask you to change it
          and send a new request. Nothing here affects devices until the certificate that carries it
          is installed.
        </Typography>

        <form onSubmit={onSubmit}>
          <Stack spacing={2} sx={{ maxWidth: 560 }}>
            <TextField
              label='Server name'
              value={serverName}
              onChange={(event) => setServerName(event.target.value)}
              helperText='Shown on every device that licenses against this server, for example "Plant 2 license server".'
              required
            />

            <Stack spacing={1}>
              {hosts.map((host, index) => (
                <Stack key={index} direction='row' spacing={1} alignItems='center'>
                  <TextField
                    label={index === 0 ? 'Hostname' : `Hostname ${index + 1}`}
                    value={host}
                    onChange={(event) => setHost(index, event.target.value)}
                    fullWidth
                    required={index === 0}
                  />
                  {hosts.length > 1 && (
                    <IconButton aria-label='Remove this hostname' onClick={() => removeHost(index)}>
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  )}
                </Stack>
              ))}

              <Typography variant='caption' color='text.secondary'>
                Host only: no <code>https://</code>, no port, no path. Not an IP address and not a
                wildcard.
              </Typography>

              {hosts.length < 5 && (
                <Button
                  size='small'
                  startIcon={<AddIcon />}
                  sx={{ alignSelf: 'flex-start' }}
                  onClick={() => setHosts(current => [...current, ''])}
                >
                  Add another hostname
                </Button>
              )}

              {hosts.length > 1 && (
                <Typography variant='caption' color='text.secondary'>
                  More than one host is for a rename, where the certificate has to name the old and
                  the new one at once, or for a server reached by two names.
                </Typography>
              )}
            </Stack>

            {error && <Alert severity='error'>{error}</Alert>}
            {saved && !error && (
              <Alert severity='success'>
                Saved. Download the enrollment request and send it to CDMVision; nothing changes for
                devices until the certificate comes back.
              </Alert>
            )}

            {differsFromCertificate && (
              <Alert severity='warning'>
                These differ from the certificate in force, which names{' '}
                <code>{certificateHosts.join(', ')}</code>. That is what devices check today.
                <strong> Do not change DNS until the new certificate is installed</strong>, or every
                device stops at once.
              </Alert>
            )}

            <Stack direction='row' spacing={2} alignItems='center'>
              <Button type='submit' variant='contained' disabled={isLoading || !isDirty}>
                {isLoading ? 'Saving…' : 'Save'}
              </Button>

              <Tooltip title={
                isDirty
                  ? 'Save first, or the request would carry the previous name and hosts.'
                  : ''
              }>
                {/* A span, because a disabled MUI button fires no events and the tooltip needs one. */}
                <span>
                  <Button
                    variant='outlined'
                    startIcon={<DownloadIcon />}
                    onClick={onDownloadRequest}
                    disabled={!canDownload}
                  >
                    Download enrollment request
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </form>
      </CardContent>
    </Card>
  );
};

export default EnrolmentDetailsCard;
